import { z } from "zod";
import { agentSystemSchema } from "../agent-tools/schemas";
import { getDefinition } from "../catalog";
import { effectiveTerminals } from "../effective";
import { getStateOption } from "../simulate/states";
import { parseAiSystemEditDraft, editDraftIsEmpty, type AiSystemEditDraft } from "../../../functions/_ai/system-edit-schema";
import type { System } from "../types";
import { ASSISTANT_MAX_CABLES, ASSISTANT_MAX_HISTORY_TURNS, ASSISTANT_MAX_NODES, ASSISTANT_MAX_PROMPT_CHARS, type AssistantContext, type AssistantEvidence, type AssistantPayload, type AssistantReply } from "./types";

// Reuse the public engine's bounded physical-field schemas. Editor metadata and
// unknown boolean settings survive intact; an assistant may not erase them.
const publicComponent = agentSystemSchema.shape.nodes.element.options[0];
const component = publicComponent.extend({
  customDefinition: publicComponent.shape.customDefinition.unwrap().loose().optional(),
}).loose();
const zone = agentSystemSchema.shape.nodes.element.options[1].loose();
const systemSchema = z.looseObject({
  ...agentSystemSchema.shape,
  nodes: z.array(z.discriminatedUnion("kind", [component, zone])).max(ASSISTANT_MAX_NODES),
  cables: z.array(agentSystemSchema.shape.cables.element.loose()).max(ASSISTANT_MAX_CABLES),
});
const reference = z.object({ code: z.string().min(1).max(160), nodeIds: z.array(z.string().max(100)).max(150), cableIds: z.array(z.string().max(100)).max(400) });
const stateKey = z.string().min(1).max(100);
const simulationSchema = z.strictObject({
  componentStates: z.record(stateKey, z.string().min(1).max(100)),
  batterySoc: z.record(stateKey, z.number().min(0).max(1)),
  loadStates: z.record(stateKey, z.strictObject({ active: z.boolean() })),
}).refine(value => Object.values(value).every(map => Object.keys(map).length <= ASSISTANT_MAX_NODES));
const payloadSchema = z.object({
  requestId: z.uuid(), conversationId: z.uuid(), prompt: z.string().trim().min(1).max(ASSISTANT_MAX_PROMPT_CHARS),
  mode: z.enum(["ask", "edit"]),
  history: z.array(z.object({ role: z.enum(["user", "assistant"]), content: z.string().max(8000) })).max(ASSISTANT_MAX_HISTORY_TURNS),
  system: systemSchema, issue: reference.optional(), projectId: z.uuid().nullable().optional(),
  locale: z.enum(["en", "sv", "de"]).optional(), autoSizeTargetPct: z.union([z.literal(3), z.literal(5)]).optional(),
  simulation: simulationSchema.optional(),
});

/** Reject hostile objects, cycles and oversized metadata before schema parsing. */
function boundedJson(raw: unknown, depth = 0): boolean {
  if (depth > 24) return false;
  if (raw == null || typeof raw === "boolean") return true;
  if (typeof raw === "number") return Number.isFinite(raw);
  if (typeof raw === "string") return raw.length <= 16000;
  if (Array.isArray(raw)) return raw.length <= 1000 && raw.every(v => boundedJson(v, depth + 1));
  if (typeof raw !== "object" || Object.getPrototypeOf(raw) !== Object.prototype) return false;
  const entries = Object.entries(raw);
  return entries.length <= 1000 && entries.every(([k, v]) => !["__proto__", "constructor", "prototype"].includes(k) && boundedJson(v, depth + 1));
}

export function parseAssistantPayload(raw: unknown): AssistantPayload | null {
  try {
    if (!boundedJson(raw) || JSON.stringify(raw).length > 512_000) return null;
    const parsed = payloadSchema.safeParse(raw);
    if (!parsed.success || parsed.data.history.reduce((n, turn) => n + turn.content.length, 0) > 32000) return null;
    const payload = parsed.data as AssistantPayload;
    return assistantSystemIsValid(payload.system) && assistantSimulationIsValid(payload) ? payload : null;
  } catch { return null; }
}

/** IDs and state names are checked against the supplied drawing, not trusted
 * just because the caller sent a correctly shaped record. */
function assistantSimulationIsValid(payload: AssistantPayload): boolean {
  if (!payload.simulation) return true;
  const definitions = new Map(payload.system.nodes.flatMap(node => node.kind === "component"
    ? [[node.id, getDefinition(node.definitionId, node.customDefinition)] as const] : []));
  const { componentStates, batterySoc, loadStates } = payload.simulation;
  return Object.entries(componentStates).every(([id, state]) => {
    const def = definitions.get(id);
    return def && getStateOption(def, state) !== undefined;
  }) && Object.keys(batterySoc).every(id => definitions.get(id)?.category === "battery")
    && Object.keys(loadStates).every(id => definitions.get(id)?.category === "load");
}

export function assistantSystemIsValid(system: System): boolean {
  const nodes = new Map(system.nodes.map(n => [n.id, n]));
  if (nodes.size !== system.nodes.length || new Set(system.cables.map(c => c.id)).size !== system.cables.length) return false;
  for (const node of system.nodes) {
    const seen = new Set([node.id]);
    let parent = node.parentId;
    while (parent) {
      if (seen.has(parent) || nodes.get(parent)?.kind !== "zone") return false;
      seen.add(parent); parent = nodes.get(parent)?.parentId;
    }
    if (node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def || (node.definitionId === "custom" && !node.customDefinition)) return false;
    const terms = effectiveTerminals(def, node);
    const ids = new Set(terms.map(t => t.id));
    if (ids.size !== terms.length) return false;
    const linked = [...(def.internalDcConnections ?? []), ...(def.oneWayDcConnections ?? []), ...(def.interchangeableTerminalGroups ?? []), ...(def.simulateStates?.options.flatMap(o => o.intraLinks ?? []) ?? [])].flat();
    if (linked.some(id => !ids.has(id))) return false;
    if (def.currentShunt && ![def.currentShunt.batteryTerminalId, def.currentShunt.systemTerminalId, def.currentShunt.referenceTerminalId].filter(Boolean).every(id => ids.has(id!))) return false;
    if (def.simulateStates && !def.simulateStates.options.some(s => s.id === def.simulateStates!.default)) return false;
  }
  // Saved drawings may contain disconnected cable ends. The engine reports
  // these as repairable diagnostics; proposal validation checks new wiring.
  return true;
}

const replySchema = z.strictObject({
  kind: z.enum(["answer", "clarification", "proposal", "unsupported"]),
  message: z.string().trim().min(1).max(8000), evidenceIds: z.array(z.string().max(300)).max(12),
  proposal: z.unknown().optional(), capability: z.string().min(1).max(100), limitation: z.string().max(1000).nullable().optional(),
  canChooseForMe: z.boolean().optional(),
  choices: z.array(z.string().trim().min(1).max(600)).max(4).refine(choices => choices.length !== 1, "Provide either no choices or 2–4 choices").optional(),
});
const proposalSchema = z.strictObject({ verifiedSpecIds: z.array(z.string().max(300)).max(24).optional(), replacements: z.array(z.strictObject({ ref: z.string().min(1).max(100), catalogId: z.string().min(1).max(100) })).max(8).optional(), topology: z.unknown().nullable().optional(), repairIssueIds: z.array(z.string().max(300)).max(16) });

/** Raw quantities in prose are refused: only cited facts can produce them.
 * Product names (e.g. Orion 12/12) and ordinary list numbering remain usable. */
const RAW_ELECTRICAL_QUANTITY = /\b\d+(?:[.,]\d+)?\s*(?:mm(?:²|2)|AWG|procent\b|prozent\b|percent\b|mA\b|milliamps?\b|milliamperes?\b|amps?\b|amperes?\b|volts?\b|watts?\b|hours?\b|Ah\b|kWh\b|Wh\b|kW\b|A\b|V\b|W\b|h\b|%)/i;

function resolveMessage(text: string, evidence: AssistantEvidence[]): { message: string; errors: string[] } {
  const errors: string[] = [];
  const bareText = text.replace(/\{\{([^{}]+)\}\}/g, "");
  const uncited = [...new Set(bareText.match(new RegExp(RAW_ELECTRICAL_QUANTITY.source, "gi")) ?? [])].slice(0, 6);
  if (uncited.length) errors.push(`The message contains an uncited electrical quantity: ${uncited.map(value => JSON.stringify(value)).join(", ")}. Remove those literal ratings. Every electrical rating, including ratings in product names, must use a listed numeric fact token and its unit; otherwise omit the figure. A clarification can simply ask for the model names without any ratings.`);
  const message = text.replace(/\{\{([^{}]+)\}\}/g, (_whole, token: string, offset: number) => {
    const split = token.lastIndexOf(".");
    const source = evidence.find(e => e.id === token.slice(0, split));
    const fact = source?.facts?.[token.slice(split + 1)];
    const expectedUnit = source?.factUnits?.[token.slice(split + 1)];
    const unit = text.slice(offset + _whole.length).match(/^\s*(mm²|mm2|Ah|mA|milliamperes?|milliamps?|amperes?|amps?|A|volts?|V|Wh|watts?|W|hours?|h|metres?|meters?|m|percent|procent|prozent|%)(?![\p{L}\p{N}])/iu)?.[1];
    if (fact === undefined || !expectedUnit) {
      errors.push(`Unknown numeric fact token ${token.slice(0, 160)}. Use only keys present in that source's facts and factUnits; titles and messages are plain text, never template tokens.`);
      return "";
    }
    if (!unit || normalizedUnit(unit) !== expectedUnit) {
      errors.push(`Token ${token.slice(0, 160)} must immediately be followed by its declared unit ${expectedUnit}.`);
      return "";
    }
    return String(fact);
  });
  if (/\{\{|\}\}/.test(message)) errors.push("Malformed fact placeholder.");
  return { message, errors: [...new Set(errors)].slice(0, 4) };
}

function resolveChoices(choices: string[] | undefined, evidence: AssistantEvidence[]): { choices: string[]; errors: string[] } {
  const resolved = (choices ?? []).map(choice => resolveMessage(choice, evidence));
  const labels = resolved.map(choice => choice.message.trim());
  const errors = resolved.flatMap(choice => choice.errors);
  if (labels.some(label => !label || label.length > 80 || /[\r\n]/.test(label))) errors.push("Keep each choice on one line, between 1 and 80 characters.");
  if (new Set(labels.map(label => label.toLowerCase())).size !== labels.length) errors.push("Choices must be distinct.");
  return { choices: labels, errors };
}

/** Specific retry feedback preserves the same acceptance boundary instead of
 * asking the model to guess which strict grounding check it failed. */
export function assistantReplyRejection(raw: unknown, context: AssistantContext): string {
  const parsed = replySchema.safeParse(raw);
  if (!parsed.success) return `Invalid response shape: ${parsed.error.issues.slice(0, 3).map(i => `${i.path.join(".")}: ${i.message}`).join("; ")}`;
  const evidence = parsed.data.evidenceIds.map(id => context.evidence.find(e => e.id === id));
  if (evidence.some(e => !e)) return "An evidence identifier is unknown. Copy only exact evidence IDs from the supplied context.";
  const choices = resolveChoices(parsed.data.choices, evidence as AssistantEvidence[]);
  if (choices.errors.length) return `Invalid choices: ${choices.errors.join(" ")}`;
  const resolved = resolveMessage(parsed.data.message, evidence as AssistantEvidence[]);
  return resolved.errors.join(" ") || "The proposal or response kind failed the contract. Use the exact supplied schema, current catalog IDs and refs, and only listed engine repair IDs. Proposals require edit capability and a requested change; otherwise answer or clarify.";
}

export function parseAssistantReply(raw: unknown, context: AssistantContext): AssistantReply | null {
  const parsed = replySchema.safeParse(raw);
  if (!parsed.success) return null;
  const reply = parsed.data;
  const evidence = [...new Set(reply.evidenceIds)].map(id => context.evidence.find(e => e.id === id));
  if (evidence.some(e => !e)) return null;
  const { message, errors } = resolveMessage(reply.message, evidence as AssistantEvidence[]);
  const choices = resolveChoices(reply.choices, evidence as AssistantEvidence[]);
  if (errors.length || choices.errors.length) return null;
  const result: AssistantReply = {
    kind: reply.kind, message, evidence: evidence as AssistantReply["evidence"], capability: reply.capability,
    ...(reply.limitation ? { limitation: reply.limitation } : {}),
    ...(reply.kind === "clarification" && reply.canChooseForMe === true ? { canChooseForMe: true } : {}),
    ...((reply.kind === "clarification" || reply.kind === "answer") && choices.choices.length ? { choices: choices.choices } : {}),
  };
  if (reply.kind !== "proposal") return reply.proposal == null ? result : null;
  if (context.payload.mode !== "edit") return null;
  const proposal = proposalSchema.safeParse(reply.proposal);
  if (!proposal.success) return null;
  let topology: AiSystemEditDraft | null | undefined;
  if (proposal.data.topology != null) {
    // Strict key checking is essential: the legacy parser intentionally coerces
    // values; this boundary must reject invented electrical settings instead.
    if (!validTopologyShape(proposal.data.topology)) return null;
    topology = parseAiSystemEditDraft(proposal.data.topology);
    if (!topology || topology.components.some(c => !context.catalogIds.has(c.catalogId))) return null;
    if (editDraftIsEmpty(topology)) topology = undefined;
  }
  const repairs = [...new Set(proposal.data.repairIssueIds)];
  if (repairs.some(id => !context.evidence.some(e => e.id === id && e.source === "diagnostic"))) return null;
  const replacements = proposal.data.replacements ?? [];
  if (new Set(replacements.map(r => r.ref)).size !== replacements.length || replacements.some(r => !context.refToNodeId.has(r.ref) || !context.catalogIds.has(r.catalogId) || topology?.removeComponents.includes(r.ref))) return null;
  const specUpdates = [...new Set(proposal.data.verifiedSpecIds ?? [])].map(id => {
    const source = context.evidence.find(e => e.id === id && e.source === "datasheet");
    return source?.verifiedSpec && source.url && source.nodeIds.length === 1
      ? { nodeId: source.nodeIds[0], ...source.verifiedSpec, sourceUrl: source.url, sourceTitle: source.title } : null;
  });
  if (specUpdates.some(update => !update)) return null;
  for (const id of proposal.data.verifiedSpecIds ?? []) {
    const source = context.evidence.find(e => e.id === id)!;
    if (!result.evidence.some(e => e.id === id)) result.evidence.push(source);
  }
  // Source verification never grants topology changes or equipment substitution.
  if (specUpdates.length && (topology || repairs.length || replacements.length)) return null;
  if (!topology && !repairs.length && !replacements.length && !specUpdates.length) return null;
  result.proposal = { ...(specUpdates.length ? { specUpdates: specUpdates.filter((u): u is NonNullable<typeof u> => !!u) } : {}), baseFingerprint: context.fingerprint, autoSizeTargetPct: context.payload.autoSizeTargetPct ?? 3, ...(topology ? { topology } : {}), ...(replacements.length ? { replacements } : {}), repairIssueIds: repairs };
  return result;
}

function validTopologyShape(raw: unknown): boolean {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  const o = raw as Record<string, unknown>;
  const keys = ["summary", "components", "connections", "removeComponents", "removeConnections", "assumptions"];
  if (Object.keys(o).some(k => !keys.includes(k)) || keys.some(k => !(k in o))) return false;
  if (typeof o.summary !== "string" || o.summary.length > 1000 || !Array.isArray(o.assumptions) || o.assumptions.length > 20 || o.assumptions.some(a => typeof a !== "string" || a.length > 1000)) return false;
  if (!Array.isArray(o.removeComponents) || o.removeComponents.some(r => typeof r !== "string" || r.length > 100)) return false;
  const shapes: Record<string, string[]> = {
    components: ["ref", "catalogId", "label", "count", "partEstimated", "countEstimated"],
    connections: ["from", "fromPort", "to", "toPort", "polarity", "lengthM", "lengthEstimated", "marker", "seriesLink"],
    removeConnections: ["from", "to", "polarity"],
  };
  for (const [field, allowed] of Object.entries(shapes)) {
    if (!Array.isArray(o[field])) return false;
    for (const value of o[field] as unknown[]) {
      if (!value || typeof value !== "object" || Array.isArray(value)) return false;
      const item = value as Record<string, unknown>;
      if (Object.keys(item).some(k => !allowed.includes(k)) || allowed.some(k => !(k in item))) return false;
      if (field === "components" && (typeof item.count !== "number" || !Number.isInteger(item.count) || item.count < 1 || item.count > 8)) return false;
      if (field === "connections" && (typeof item.lengthM !== "number" || !Number.isFinite(item.lengthM) || item.lengthM < .01 || item.lengthM > 1000)) return false;
      if (["partEstimated", "countEstimated", "lengthEstimated", "seriesLink"].some(k => k in item && typeof item[k] !== "boolean")) return false;
      if (["ref", "catalogId", "from", "to"].some(k => k in item && (typeof item[k] !== "string" || !(item[k] as string).length || (item[k] as string).length > 100))) return false;
      if (["label", "marker"].some(k => k in item && item[k] !== null && (typeof item[k] !== "string" || (item[k] as string).length > 200))) return false;
      if ("polarity" in item && !["positive", "negative"].includes(item.polarity as string)) return false;
      if (["fromPort", "toPort"].some(k => k in item && !["input", "output", "any"].includes(item[k] as string))) return false;
      if ("marker" in item && typeof item.marker === "string" && RAW_ELECTRICAL_QUANTITY.test(item.marker)) return false;
    }
  }
  return true;
}

function normalizedUnit(unit: string): string {
  const text = unit.toLowerCase();
  if (["a", "amp", "amps", "ampere", "amperes"].includes(text)) return "A";
  if (["ma", "milliamp", "milliamps", "milliampere", "milliamperes"].includes(text)) return "mA";
  if (text === "ah") return "Ah";
  if (["v", "volt", "volts"].includes(text)) return "V";
  if (["w", "watt", "watts"].includes(text)) return "W";
  if (text === "wh") return "Wh";
  if (["h", "hour", "hours"].includes(text)) return "h";
  if (["m", "metre", "metres", "meter", "meters"].includes(text)) return "m";
  if (["mm2", "mm²"].includes(text)) return "mm²";
  return "%";
}
